import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listGroups } from '../api/groups';
import {
  DOWNLOAD_SIZE,
  MAX_BATCH_IMAGES,
  archiveQrCode,
  createFileQrCode,
  createQrCode,
  deleteQrCode,
  downloadQrCodeImages,
  listQrCodes,
  restoreQrCode,
} from '../api/qrCodes';
import type { Group, QrCode } from '../api/types';
import { CODE_TYPES, codeTypeLabel, codeTypeOf, type CodeType } from '../codeType';
import { belongsToAGroup, useCurrentUser } from '../auth/CurrentUserProvider';
import { useDialog } from '../components/Dialog';
import { Dropdown } from '../components/Dropdown';
import { DEFAULT_BATCH_CHOICE, ImageBatchOptions } from '../components/ImageBatchOptions';
import { ErrorBanner } from '../components/ErrorBanner';
import { QrCodeForm, type QrCodeFormValues } from '../components/QrCodeForm';
import { QrCodeGrid } from '../components/QrCodeGrid';
import { useToast } from '../components/Toast';
import { saveBlob } from '../download';
import { NoGroupNotice } from './NoGroupNotice';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialog = useDialog();
  const toast = useToast();
  const { user, loading: identifying } = useCurrentUser();

  const [groups, setGroups] = useState<Group[]>([]);
  const [qrCodes, setQrCodes] = useState<QrCode[]>([]);
  const [groupFilter, setGroupFilter] = useState('');
  // Hidden until asked for: the list is the codes in service, and a retired one is not one of them.
  const [includeArchived, setIncludeArchived] = useState(false);
  const [kindFilter, setKindFilter] = useState<'' | CodeType>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<QrCode[]>([]);
  const [downloading, setDownloading] = useState(false);

  // A slow answer to an old query must never overwrite the results of a newer one.
  const latestQuery = useRef(0);

  /*
   * Everything the caller can see, fetched once - archived codes included, because the switch decides whether
   * they are shown rather than whether they are known. The filters below are then a question about a list that
   * is already here: no request per keystroke, and no table rebuilding itself around the answer.
   */
  const reload = useCallback(async () => {
    const query = ++latestQuery.current;
    setLoading(true);
    try {
      const codes = await listQrCodes({ includeArchived: true });

      if (query !== latestQuery.current) {
        return;
      }

      setQrCodes(codes);
      setError(null);
    } catch (caught) {
      if (query === latestQuery.current) {
        setError(caught);
      }
    } finally {
      if (query === latestQuery.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    listGroups().then(setGroups).catch(setError);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /*
   * The rows on screen: the same three questions the API used to be asked, asked of the list in hand. The
   * search matches what the server's did - the title, the link identifier and the destination.
   */
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return qrCodes.filter((qrCode) => {
      if (!includeArchived && qrCode.isArchived) {
        return false;
      }

      if (groupFilter && qrCode.ownerGroupId !== groupFilter) {
        return false;
      }

      if (kindFilter && codeTypeOf(qrCode) !== kindFilter) {
        return false;
      }

      if (!needle) {
        return true;
      }

      return (
        qrCode.title.toLowerCase().includes(needle) ||
        qrCode.slug.toLowerCase().includes(needle) ||
        qrCode.targetUrl.toLowerCase().includes(needle) ||
        qrCode.files.some((file) => file.isLive && file.fileName.toLowerCase().includes(needle))
      );
    });
  }, [qrCodes, search, groupFilter, kindFilter, includeArchived]);

  async function handleCreate(values: QrCodeFormValues) {
    setSubmitting(true);
    try {
      // Forwarded whole rather than field by field: picking fields here is how kind and product came to be
      // dropped, which reached the API as a destination code with no URL.
      const created =
        values.kind === 'File' && values.file
          ? await createFileQrCode({
              ownerGroupId: values.ownerGroupId,
              title: values.title,
              slug: values.slug,
              file: values.file,
            })
          : await createQrCode({
              ownerGroupId: values.ownerGroupId,
              title: values.title,
              kind: values.kind,
              targetUrl: values.targetUrl,
              languageTargets: values.languageTargets,
              product: values.product,
              slug: values.slug,
            });
      setCreating(false);
      setError(null);
      toast.success(t('toast.created'));

      // Straight to the new code rather than back to the list: what somebody wants next is its image and
      // its link, and the list is refetched anyway when they come back to it.
      navigate(`/qr/${created.id}`);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const run = useCallback(
    async (action: () => Promise<unknown>, done: string) => {
      try {
        await action();
        setError(null);
        await reload();
        toast.success(done);
      } catch (caught) {
        setError(caught);
      }
    },
    [reload, toast],
  );

  const handleArchive = useCallback(
    async (qrCode: QrCode) => {
      const confirmed = await dialog.confirm({
        title: t('qrCode.archiveTitle'),
        body: t('qrCode.archiveBody', { title: qrCode.title }),
        confirmLabel: t('qrCode.archive'),
        tone: 'primary',
        icon: 'pi-inbox',
      });

      if (confirmed) {
        await run(() => archiveQrCode(qrCode.id), t('toast.archived', { title: qrCode.title }));
      }
    },
    [dialog, t, run],
  );

  // Nothing is lost by restoring, so nothing is asked before it.
  const handleRestore = useCallback(
    (qrCode: QrCode) => run(() => restoreQrCode(qrCode.id), t('toast.restored', { title: qrCode.title })),
    [run, t],
  );

  // Reserved to super admins by the API, and warned about here: the identifier goes back into circulation, so
  // a later code could inherit it and repoint every printed copy of this one.
  const handleDelete = useCallback(
    async (qrCode: QrCode) => {
      const confirmed = await dialog.confirm({
        title: t('qrCode.deleteTitle'),
        body: t('qrCode.deleteBody', { title: qrCode.title, slug: qrCode.slug }),
        confirmLabel: t('qrCode.deleteConfirm'),
        icon: 'pi-exclamation-triangle',
      });

      if (confirmed) {
        await run(() => deleteQrCode(qrCode.id), t('toast.deleted', { title: qrCode.title }));
      }
    },
    [dialog, t, run],
  );

  const canCreate = groups.some((group) => group.canEditQrCodes);

  async function handleDownloadImages() {
    const downloadCount = selected.length;

    if (downloadCount > MAX_BATCH_IMAGES) {
      await dialog.alert({
        title: t('dashboard.downloadTitle'),
        body: (
          <div className="alert alert-danger mb-0" role="alert">
            {t('dashboard.downloadTooMany', { max: MAX_BATCH_IMAGES, count: downloadCount })}
          </div>
        ),
        tone: 'danger',
      });
      return;
    }

    let choice = DEFAULT_BATCH_CHOICE;

    const confirmed = await dialog.confirm({
      title: t('dashboard.downloadTitle'),
      body: (
        <>
          <p>{t('dashboard.downloadSelected', { count: downloadCount })}</p>
          <ImageBatchOptions previewCode={selected[0]} onChange={(next) => (choice = next)} />
        </>
      ),
      confirmLabel: t('dashboard.downloadConfirm', { count: downloadCount }),
      tone: 'primary',
      icon: 'pi-download',
    });

    if (!confirmed) {
      return;
    }

    setDownloading(true);
    const dismissProgress = toast.show({
      message: t('toast.imagesGenerating', { count: downloadCount }),
      tone: 'info',
      icon: 'pi-spin pi-spinner',
      duration: null,
    });
    try {
      const blob = await downloadQrCodeImages({ ids: selected.map((qrCode) => qrCode.id) }, {
        format: choice.format,
        size: DOWNLOAD_SIZE[choice.format],
        ...choice.style,
      });
      saveBlob(blob, `qr-codes-${new Date().toISOString().slice(0, 10)}.zip`);
      setError(null);
      toast.success(t('toast.imagesDownloaded', { count: downloadCount }));
    } catch (caught) {
      setError(caught);
    } finally {
      dismissProgress();
      setDownloading(false);
    }
  }

  /*
   * Nothing to filter and nothing to list: somebody who belongs to no group has no codes to see, and cannot be
   * given any by a search box, so the whole page is the explanation instead. It waits for the answer before
   * deciding, so the explanation is not shown to somebody who has codes and then taken away again.
   */
  if (!identifying && !belongsToAGroup(user) && !user?.personalSpaceId) {
    return (
      <>
        <ErrorBanner error={error} />
        <NoGroupNotice supportEmail={user?.supportEmail ?? ''} />
      </>
    );
  }

  return (
    <>
      <ErrorBanner error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field">
            <label htmlFor="filter-group" id="filter-group-label">
              <i className="pi pi-users me-1" aria-hidden="true" />
              {t('dashboard.filterByGroup')}
            </label>
            <Dropdown
              id="filter-group"
              labelledBy="filter-group-label"
              variant="field"
              value={groupFilter}
              options={[
                { value: '', label: t('dashboard.allGroups') },
                ...groups.map((group) => ({ value: group.id, label: group.name })),
                ...(user?.personalSpaceId ? [{ value: user.personalSpaceId, label: t('qrCode.personalSpace') }] : []),
              ]}
              onChange={setGroupFilter}
            />
          </div>

          <div className="field">
            <label htmlFor="filter-kind" id="filter-kind-label">
              <i className="pi pi-tag me-1" aria-hidden="true" />
              {t('dashboard.filterByKind')}
            </label>
            <Dropdown
              id="filter-kind"
              labelledBy="filter-kind-label"
              variant="field"
              value={kindFilter}
              options={[
                { value: '', label: t('dashboard.allKinds') },
                ...CODE_TYPES.map((type) => ({ value: type, label: codeTypeLabel(type, t) })),
              ]}
              onChange={(value) => setKindFilter(value as '' | CodeType)}
            />
          </div>

          <div className="field flex-grow-1">
            <label htmlFor="filter-search">
              <i className="pi pi-search me-1" aria-hidden="true" />
              {t('common.search')}
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="pi pi-search" aria-hidden="true" />
              </span>
              <input
                id="filter-search"
                type="search"
                className="form-control"
                value={search}
                placeholder={t('dashboard.searchPlaceholder')}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="form-check form-switch align-self-end mb-2">
            <input
              id="filter-archived"
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="filter-archived">
              {t('qrCode.showArchived')}
            </label>
          </div>

          {!creating && (
            <button
              type="button"
              className="btn btn-primary align-self-end"
              onClick={() => setCreating(true)}
              disabled={!canCreate}
            >
              <i className="pi pi-plus me-2" aria-hidden="true" />
              {t('dashboard.newQrCode')}
            </button>
          )}
        </div>

        {!canCreate && groups.length > 0 && (
          <div className="alert alert-info d-flex gap-2 mb-0" role="status">
            <i className="pi pi-info-circle mt-1" aria-hidden="true" />
            <span>{t('dashboard.readOnlyNotice')}</span>
          </div>
        )}
      </div>

      {creating && (
        <div className="card">
          <h2>
            <i className="pi pi-plus-circle me-2" aria-hidden="true" />
            {t('qrCode.createTitle')}
          </h2>
          <QrCodeForm
            groups={groups}
            submitting={submitting}
            onSubmit={handleCreate}
            onCancel={() => setCreating(false)}
          />
        </div>
      )}

      <div className="card">
        <div className="card-heading">
          <h2>
            <i className="pi pi-qrcode me-2" aria-hidden="true" />
            {t('dashboard.title')}
          </h2>
          <span className="text-secondary small">
            {t('dashboard.resultCount', { count: visible.length })}
          </span>
          {selected.length > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ms-auto"
              onClick={() => void handleDownloadImages()}
              disabled={downloading}
            >
              <i className={`pi ${downloading ? 'pi-spin pi-spinner' : 'pi-download'} me-2`} aria-hidden="true" />
              {t('dashboard.downloadImages', { count: selected.length })}
            </button>
          )}
        </div>

        {/* The grid stays mounted while a query runs, so a refreshed result replaces rows rather than the
            whole page. */}
        <QrCodeGrid
          qrCodes={visible}
          loading={loading}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onDelete={handleDelete}
          onSelectionChange={setSelected}
          emptyMessage={t('dashboard.noMatch')}
          showProduct={kindFilter === 'Product'}
        />
      </div>
    </>
  );
}
